import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Search, Save, Eye, Pencil, Crown, Calculator, HardHat, Users, Ban, Sparkles, LayoutDashboard } from 'lucide-react';
import { DASHBOARD_WIDGETS } from '@/config/dashboardWidgets';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESET_ICONS = { Calculator, HardHat, Users, Ban, Eye, Crown };

export default function GestioneAdminPage() {
  const [admins, setAdmins] = useState([]);
  const [sections, setSections] = useState([]);
  const [presets, setPresets] = useState([]);  // iter65
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);  // admin object
  const [editPerms, setEditPerms] = useState({});
  const [saving, setSaving] = useState(false);
  // iter103: personalizzazione dashboard per admin
  const [dashEditing, setDashEditing] = useState(null);
  const [dashHidden, setDashHidden] = useState([]);
  const [dashSaving, setDashSaving] = useState(false);

  const openDashEdit = async (a) => {
    try {
      const r = await axios.get(`${API}/admin/dashboard-config/${a.id}`, { withCredentials: true });
      setDashHidden(r.data?.hidden || []);
      setDashEditing(a);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento config dashboard');
    }
  };
  const toggleWidget = (key) => {
    setDashHidden((h) => h.includes(key) ? h.filter((k) => k !== key) : [...h, key]);
  };
  const saveDashConfig = async () => {
    setDashSaving(true);
    try {
      await axios.put(`${API}/admin/dashboard-config/${dashEditing.id}`, { hidden: dashHidden }, { withCredentials: true });
      toast.success('Dashboard personalizzata salvata');
      setDashEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setDashSaving(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aRes, sRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/permissions/users`, { withCredentials: true }),
        axios.get(`${API}/permissions/sections`, { withCredentials: true }),
        axios.get(`${API}/permissions/presets`, { withCredentials: true }),
      ]);
      setAdmins(aRes.data?.admins || []);
      setSections(sRes.data?.sections || []);
      setPresets(pRes.data?.presets || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento admin');
    } finally {
      setLoading(false);
    }
  };

  // iter65: applica preset alle checkbox in edit corrente
  const applyPreset = (preset) => {
    setEditPerms(JSON.parse(JSON.stringify(preset.permissions || {})));
    toast.info(`Preset "${preset.label}" applicato — ricordati di salvare`);
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return admins;
    const q = query.toLowerCase();
    return admins.filter(a =>
      (a.email || '').toLowerCase().includes(q) ||
      (a.name || '').toLowerCase().includes(q)
    );
  }, [admins, query]);

  // Raggruppa sezioni per gruppo
  const sectionsGrouped = useMemo(() => {
    const groups = {};
    sections.forEach(s => {
      if (!groups[s.group]) groups[s.group] = [];
      groups[s.group].push(s);
    });
    return groups;
  }, [sections]);

  const openEdit = (admin) => {
    if (admin.is_super_admin) {
      toast.info('Il super-admin ha permessi immutabili — non modificabili');
      return;
    }
    setEditing(admin);
    setEditPerms(JSON.parse(JSON.stringify(admin.permissions || {})));
  };

  const togglePerm = (sectionKey, level) => {
    setEditPerms(prev => {
      const cur = prev[sectionKey] || { view: false, edit: false };
      const next = { ...cur, [level]: !cur[level] };
      // Se disattivo view, disattivo anche edit
      if (level === 'view' && !next.view) next.edit = false;
      // Se attivo edit, attivo anche view
      if (level === 'edit' && next.edit) next.view = true;
      return { ...prev, [sectionKey]: next };
    });
  };

  const setAllInGroup = (groupName, level, value) => {
    setEditPerms(prev => {
      const next = { ...prev };
      sectionsGrouped[groupName].forEach(s => {
        const cur = next[s.key] || { view: false, edit: false };
        next[s.key] = { ...cur, [level]: value };
        // consistency: se disattivo view, disattivo edit
        if (level === 'view' && !value) next[s.key].edit = false;
        // se attivo edit, attivo view
        if (level === 'edit' && value) next[s.key].view = true;
      });
      return next;
    });
  };

  const setAll = (level, value) => {
    setEditPerms(prev => {
      const next = { ...prev };
      sections.forEach(s => {
        const cur = next[s.key] || { view: false, edit: false };
        next[s.key] = { ...cur, [level]: value };
        if (level === 'view' && !value) next[s.key].edit = false;
        if (level === 'edit' && value) next[s.key].view = true;
      });
      return next;
    });
  };

  const savePermissions = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/permissions/${encodeURIComponent(editing.email)}`,
        { permissions: editPerms },
        { withCredentials: true }
      );
      toast.success(`Permessi aggiornati per ${editing.email}`);
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="gestione-admin-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Shield className="h-7 w-7 text-slate-700" />
          Gestione Admin &amp; Permessi
        </h1>
        <p className="text-slate-500 mt-1">Controlla quali sezioni può usare ogni admin. Il super-admin (F. Conti) ha permessi immutabili.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              placeholder="Cerca per email o nome…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-72"
              data-testid="admin-search"
            />
            <span className="text-xs text-slate-500 ml-2">{filtered.length} admin</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(a => (
          <Card
            key={a.email}
            className={`border ${a.is_super_admin ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:border-slate-300 cursor-pointer'} transition-colors`}
            onClick={() => !a.is_super_admin && openEdit(a)}
            data-testid={`admin-card-${a.email}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900 truncate">{a.name || '—'}</p>
                  <p className="text-xs text-slate-500 truncate">{a.email}</p>
                </div>
                {a.is_super_admin ? (
                  <Badge className="bg-amber-500 text-white border-amber-600 shrink-0">
                    <Crown className="h-3 w-3 mr-1" />Super
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {a.status === 'attivo' ? '✓ Attivo' : a.status}
                  </Badge>
                )}
              </div>
              {a.is_super_admin ? (
                <p className="text-xs text-amber-700 italic">Accesso totale • Immutabile</p>
              ) : (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(() => {
                    const perms = a.permissions || {};
                    const total = sections.length;
                    const viewAll = sections.every(s => perms[s.key]?.view);
                    const editAll = sections.every(s => perms[s.key]?.edit);
                    const nView = sections.filter(s => perms[s.key]?.view).length;
                    const nEdit = sections.filter(s => perms[s.key]?.edit).length;
                    if (viewAll && editAll) return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Accesso completo</Badge>;
                    return (
                      <>
                        <Badge variant="outline" className="text-[10px]"><Eye className="h-2.5 w-2.5 mr-0.5" />{nView}/{total}</Badge>
                        <Badge variant="outline" className="text-[10px]"><Pencil className="h-2.5 w-2.5 mr-0.5" />{nEdit}/{total}</Badge>
                      </>
                    );
                  })()}
                </div>
              )}
              {!a.is_super_admin && (
                <Button
                  size="sm" variant="outline"
                  className="w-full mt-3 text-xs"
                  onClick={(e) => { e.stopPropagation(); openDashEdit(a); }}
                  data-testid={`dashboard-config-btn-${a.email}`}
                >
                  <LayoutDashboard className="h-3.5 w-3.5 mr-1" /> Personalizza dashboard
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* iter103: Dialog personalizzazione dashboard per admin */}
      <Dialog open={!!dashEditing} onOpenChange={(o) => !o && setDashEditing(null)}>
        <DialogContent className="max-w-md" data-testid="dashboard-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutDashboard className="h-5 w-5 text-blue-600" /> Dashboard di {dashEditing?.name || dashEditing?.email}
            </DialogTitle>
            <DialogDescription>Scegli quali widget sono visibili nella dashboard di questo admin.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">Deseleziona i widget che vuoi nascondere a questo admin.</p>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {DASHBOARD_WIDGETS.map((w) => {
              const visible = !dashHidden.includes(w.key);
              return (
                <label key={w.key} className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-slate-50 cursor-pointer" data-testid={`dashboard-widget-${w.key}`}>
                  <span className="text-sm text-slate-700">{w.label}</span>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => toggleWidget(w.key)}
                    className="h-4 w-4 accent-blue-600"
                    data-testid={`dashboard-widget-toggle-${w.key}`}
                  />
                </label>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDashEditing(null)}>Annulla</Button>
            <Button onClick={saveDashConfig} disabled={dashSaving} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="dashboard-config-save">
              <Save className="h-4 w-4 mr-1" /> Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Permessi */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="permessi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              Modifica Permessi: <span className="text-blue-700">{editing?.email}</span>
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {/* iter65: Preset ruoli rapidi */}
              {presets.length > 0 && (
                <div className="border-2 border-blue-200 bg-blue-50 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">Preset ruoli rapidi</span>
                    <span className="text-[10px] text-blue-600 ml-1">Click per applicare uno schema predefinito</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {presets.map(p => {
                      const Icon = PRESET_ICONS[p.icon] || Shield;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => applyPreset(p)}
                          className="border border-blue-300 bg-white hover:bg-blue-100 rounded-md p-2 text-left transition-colors"
                          data-testid={`preset-${p.key}`}
                          title={p.description}
                        >
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Icon className="h-3.5 w-3.5 text-blue-600" />
                            <span className="text-xs font-semibold text-slate-800">{p.label}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 leading-tight line-clamp-2">{p.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Controlli bulk */}
              <div className="bg-slate-50 border rounded-md p-3 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-slate-700">Azioni rapide:</span>
                <Button size="sm" variant="outline" onClick={() => setAll('view', true)} data-testid="bulk-view-all">Attiva tutti View</Button>
                <Button size="sm" variant="outline" onClick={() => setAll('edit', true)} data-testid="bulk-edit-all">Attiva tutti Edit</Button>
                <Button size="sm" variant="outline" onClick={() => setAll('view', false)} className="text-red-600 border-red-200" data-testid="bulk-clear-all">Disattiva tutto</Button>
              </div>

              {/* Matrice permessi per gruppo */}
              {Object.entries(sectionsGrouped).map(([groupName, groupSections]) => (
                <div key={groupName} className="border rounded-md">
                  <div className="bg-slate-100 px-3 py-2 border-b flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">{groupName}</h3>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setAllInGroup(groupName, 'view', true)}>Tutti View</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setAllInGroup(groupName, 'edit', true)}>Tutti Edit</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-600" onClick={() => setAllInGroup(groupName, 'view', false)}>Nessuno</Button>
                    </div>
                  </div>
                  <div className="divide-y">
                    {groupSections.map(s => {
                      const p = editPerms[s.key] || { view: false, edit: false };
                      return (
                        <div key={s.key} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                          <span className="text-sm text-slate-700">{s.label}</span>
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`perm-${s.key}-view`}>
                              <input
                                type="checkbox"
                                checked={p.view}
                                onChange={() => togglePerm(s.key, 'view')}
                                className="rounded"
                              />
                              <Eye className="h-3 w-3 text-slate-500" /> View
                            </label>
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`perm-${s.key}-edit`}>
                              <input
                                type="checkbox"
                                checked={p.edit}
                                onChange={() => togglePerm(s.key, 'edit')}
                                className="rounded"
                              />
                              <Pencil className="h-3 w-3 text-slate-500" /> Edit
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-2">
                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annulla</Button>
                <Button onClick={savePermissions} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700" data-testid="save-permessi-btn">
                  <Save className="h-4 w-4 mr-1.5" />{saving ? 'Salvataggio…' : 'Salva permessi'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
